import React, { useEffect, useState } from "react";
import Avatar from "../../../../../components/reusable/Avatar";
import RightSideDrawer from "../../../../../components/reusable/RightSideDrawer";
import DrawerSection from "../../../../../components/reusable/DrawerSection";
import StatusBadge from "../../../../../utils/StatusBadge";

const EmployeeDrawer = ({
    isOpen,
    onClose,
    employee,
    loading = false,
    error = null,
    onUpdate,
}) => {
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState({});

    useEffect(() => {
        if (!employee) {
            setForm({});
            setEditMode(false);
            return;
        }

        setForm({
            user_full_name_display: employee.user_full_name_display || "",
            email: employee.user_email || "",
            contact_display: employee.contact_display || "",
            designation: employee.designation || "",
            office: employee.office || "",
            department: employee.department || "",
            team: employee.team || "",
            reporting_manager: employee.reporting_manager || "",
            joining_date: employee.joining_date || "",
        });

        setEditMode(false);
    }, [employee]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((previous) => ({ ...previous, [name]: value }));
    };

    const handleEdit = () => { setEditMode(true); };

    const handleCancel = () => {
        if (!employee) { return; }

        setForm({
            user_full_name_display: employee.user_full_name_display || "",
            email: employee.user_email || "",
            contact_display: employee.contact_display || "",
            designation: employee.designation || "",
            office: employee.office || "",
            department: employee.department || "",
            team: employee.team || "",
            reporting_manager: employee.reporting_manager || "",
            joining_date: employee.joining_date || "",
        });

        setEditMode(false);
    };

    const handleSave = async () => {
        if (!employee) { return; }

        const payload = {
            user_full_name: form.user_full_name_display.trim(),
            email: form.email.trim(),
            contact: form.contact_display.trim(),
            designation: form.designation.trim(),
            office: form.office || null,
            department: form.department || null,
            team: form.team || null,
            reporting_manager: form.reporting_manager || null,
            joining_date: form.joining_date || null,
        };

        const result = await onUpdate?.(employee.id, payload);

        if (result?.success) { setEditMode(false); }
    };

    if (!employee) { return null; }
    console.log("EmployeeDrawer: Rendering with employee:", employee);

    return (
        <RightSideDrawer
            isOpen={isOpen}
            onClose={onClose}
            title="Employee Overview"
            width="550px"
        >
            {/* HEADER */}
            <div className="employee-drawer-header">
                <Avatar
                    image = { employee.profile_image || "" }
                    name  = { employee.user_full_name_display || employee.user_email }
                    size  = "lg"
                />

                <h2> { employee.user_full_name_display || "Unnamed Employee" }</h2>
                <p>{ employee.designation || "Employee" }</p>
                <span>{ employee.employee_code || "No employee code" }</span>
            </div>

            {error && (
                <div className="employee-drawer-error">
                    { error }
                </div>
            )}

            {/* EDIT ACTIONS */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    marginBottom: "20px",
                }}
            >
                {!editMode ? (
                    <button
                        type="button"
                        onClick={handleEdit}
                        disabled={loading}
                    >
                        Edit
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : "Save Changes"}
                        </button>
                    </>
                )}
            </div>

            {/* CONTACT */}
            <DrawerSection title="Contact">
                {editMode ? (
                    <>
                        <div>
                            <label>Full Name</label>

                            <input
                                type="text"
                                name="user_full_name_display"
                                value={ form.user_full_name_display || "" }
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label>Email</label>

                            <input
                                type="email"
                                name="email"
                                value={form.email || ""}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label>Contact</label>

                            <input
                                type="text"
                                name="contact_display"
                                value={ form.contact_display || "" }
                                onChange={handleChange}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <p>
                            <strong>Email:</strong>{" "}
                            {employee.user_email || "—"}
                        </p>

                        <p>
                            <strong>Contact:</strong>{" "}
                            {employee.contact_display || "—"}
                        </p>
                    </>
                )}
            </DrawerSection>

            {/* ORGANIZATION */}
            <DrawerSection title="Organization">
                {editMode ? (
                    <>
                        <div>
                            <label>Designation</label>

                            <input
                                type="text"
                                name="designation"
                                value={ form.designation || "" }
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label>Office</label>

                            <select
                                name="office"
                                value={form.office || ""}
                                onChange={handleChange}
                            >
                                <option value="">Select office</option>

                                {/* 
                                    Add office options here
                                    once EmployeePage provides
                                    them to the drawer.
                                */}
                            </select>
                        </div>

                        <div>
                            <label>Department</label>

                            <select
                                name="department"
                                value={ form.department || "" }
                                onChange={handleChange}
                            >
                                <option value="">Select department</option>
                            </select>
                        </div>

                        <div>
                            <label>Team</label>

                            <select
                                name="team"
                                value={form.team || ""}
                                onChange={handleChange}
                            >
                                <option value="">Select team</option>
                            </select>
                        </div>

                        <div>
                            <label>Reporting Manager</label>

                            <select
                                name="reporting_manager"
                                value={ form.reporting_manager || "" }
                                onChange={handleChange}
                            >
                                <option value="">No reporting manager</option>
                            </select>
                        </div>
                    </>
                ) : (
                    <>
                        <p>
                            <strong>Office:</strong>{" "}
                            {employee.office_name || "—"}
                        </p>

                        <p>
                            <strong>Department:</strong>{" "}
                            {employee.department_name || "—"}
                        </p>

                        <p>
                            <strong>Team:</strong>{" "}
                            {employee.team_name || "—"}
                        </p>

                        <p>
                            <strong>
                                Reporting Manager:
                            </strong>{" "}
                            {employee.reporting_manager_name ||
                                "—"}
                        </p>
                    </>
                )}
            </DrawerSection>

            {/* EMPLOYMENT */}
            <DrawerSection title="Employment">
                {editMode ? (
                    <div>
                        <label>Joining Date</label>

                        <input
                            type="date"
                            name="joining_date"
                            value={ form.joining_date || "" }
                            onChange={handleChange}
                        />
                    </div>
                ) : (
                    <>
                        <p>
                            <strong>
                                Employee Code:
                            </strong>{" "}
                            {employee.employee_code || "—"}
                        </p>

                        <p>
                            <strong>
                                Joining Date:
                            </strong>{" "}
                            {employee.joining_date || "—"}
                        </p>

                        <p>
                            <strong>Status:</strong>{" "}
                            <StatusBadge status={employee.status} />
                        </p>
                    </>
                )}
            </DrawerSection>

            {/* ROLES */}
            <DrawerSection title="Roles">
                {employee.roles?.length ? (
                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                        }}
                    >
                        {employee.roles.map((role) => (
                            <span
                                key={role.id}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "999px",
                                    background: "#f1f5f9",
                                    fontSize: "13px",
                                }}
                            >
                                {role.role_name || "Role"}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p>No roles assigned.</p>
                )}
            </DrawerSection>
        </RightSideDrawer>
    );
};

export default EmployeeDrawer;
